/**
 * ONE TAB STOP FOR A WHOLE SIDEBAR, AND THE ARROW KEYS INSIDE IT - the keyboard half of the
 * `NavRail` row both apps' navigation owes (`docs/ui/03-components.md`, `:88-89`): "real `<a>`
 * elements, one Tab stop for the whole list with Up/Down between items, `aria-current="page"` on
 * the active one".
 *
 * WHY IT WAS OWED. Both sidebars already drew Mantine's `NavLink` as a real `<a href>` with
 * `aria-current`, and every entry was its own Tab stop: twelve presses through the console's list,
 * eight through the cabinet's, before a keyboard reached the screen. The DataTable header had
 * already solved the same problem for its controls (`../DataTable/DataTable.tsx`); this is
 * that model for a vertical list of links, written once and imported by both apps.
 *
 * WHAT IT DOES, AND WHAT IT LEAVES ALONE
 *   - Exactly one entry is in the Tab order (`tabIndex` 0): the one the reader last moved to inside
 *     the list on this page, else the current page's entry, else the first. Every other entry is
 *     -1 - still focusable, still in a screen reader's list of links, still clickable.
 *   - Down and Up move focus to the next and the previous entry; Home and End to the first and last.
 *   - It never touches a click and never handles Enter. The entries stay real `<a href>`, so Enter
 *     follows the link natively, and a middle click, a modified click or "open in new tab" are the
 *     browser's exactly as before. `aria-current` stays the caller's to set.
 *   - A key pressed with Alt, Ctrl, Meta or Shift is left to the browser and the screen reader.
 *
 * ★ UP AND DOWN WRAP, THEY DO NOT CLAMP. Down on the last entry goes to the first. It is what the
 * repository's one other roving group already does - DataTable's Left and Right move by
 * `(from + delta + length) % length` - so a keyboard user meets one behaviour, not two; a press that
 * does nothing at an edge reads as a dead key, where a focus ring arriving at the top shows where
 * the list begins again; and Home and End still give the absolute ends. A list of at most twelve
 * entries never makes the wrap a long way round.
 *
 * ★ A NEW PAGE MOVES THE STOP BACK TO ITS ENTRY. Where the reader moved to is remembered only for
 * the page they moved on. After a navigation the Tab stop is the new current entry, so the next Tab
 * into the list lands where the reader is, not on an entry they arrowed past three pages ago - which
 * matters in the back office, whose chrome stays mounted across its own screens.
 *
 * ★ NO EFFECTS, AND THAT IS WHAT MAKES A REMOUNT SAFE. The entries' elements are held by React 19
 * ref callbacks that return their own cleanup, and the stop is plain state. React detaches host refs
 * when an `<Activity>` hides its subtree and attaches them again when it shows it
 * (`react-dom` 19.2, `disappearLayoutEffects` / `safelyDetachRef`), and StrictMode runs the same
 * detach and attach once on mount. `test/components/NavRail/use-roving-nav.spec.tsx` hides and shows
 * a mounted list with `<Activity>` and drives the keys afterwards, because a StrictMode render in
 * this suite is no evidence: a hook that only works on its first mount passes every other test.
 */
import { useState } from 'react';
import type { KeyboardEvent, RefCallback } from 'react';

export interface RovingNavOptions {
  /** How many entries the list draws. An entry's index is its position in that order. */
  readonly count: number;
  /** The index of the current page's entry, or -1 when the page is none of them. */
  readonly currentIndex: number;
}

/**
 * What one entry spreads onto its own `<a>`. Mantine's `NavLink` hands every one of them to the
 * anchor it renders, and merges `onKeyDown` with its own (`@mantine/core` 9.5, `NavLink.mjs`).
 */
export interface RovingNavItemProps {
  readonly ref: RefCallback<HTMLElement>;
  readonly tabIndex: 0 | -1;
  readonly onFocus: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/** Where the reader moved the stop to, and on which page they did it. */
interface Moved {
  readonly index: number;
  readonly onPage: number;
}

/**
 * The entry a key moves focus to from `from`, or `null` for a key this list does not own. Exported
 * for its spec; a screen has no reason to call it.
 */
export function rovingTarget(key: string, from: number, count: number): number | null {
  if (count <= 0) {
    return null;
  }
  const last = count - 1;
  switch (key) {
    case 'ArrowDown':
      return from >= last ? 0 : from + 1;
    case 'ArrowUp':
      return from <= 0 ? last : from - 1;
    case 'Home':
      return 0;
    case 'End':
      return last;
    default:
      return null;
  }
}

/**
 * The roving tab stop for one list. Call it once per render of the list and spread
 * `itemProps(index)` onto each entry's `<a>`, in the order the list draws them.
 */
export function useRovingNav({
  count,
  currentIndex,
}: RovingNavOptions): (index: number) => RovingNavItemProps {
  // Two containers made once per mount and written only by React's own ref calls, never read to
  // decide what to render. The ref callbacks are kept per index so their identity is stable and
  // React does not detach and re-attach every entry on every render.
  const [elements] = useState(() => new Map<number, HTMLElement>());
  const [refs] = useState(() => new Map<number, RefCallback<HTMLElement>>());
  const [moved, setMoved] = useState<Moved | null>(null);

  const home = currentIndex >= 0 && currentIndex < count ? currentIndex : 0;
  const stop =
    moved !== null && moved.onPage === currentIndex && moved.index < count ? moved.index : home;

  const refFor = (index: number): RefCallback<HTMLElement> => {
    const known = refs.get(index);
    if (known !== undefined) {
      return known;
    }
    const attach: RefCallback<HTMLElement> = (element) => {
      if (element === null) {
        return undefined;
      }
      elements.set(index, element);
      return () => {
        // Only the element this call attached: when entries move, the next one may already be here.
        if (elements.get(index) === element) {
          elements.delete(index);
        }
      };
    };
    refs.set(index, attach);
    return attach;
  };

  const moveTo = (index: number): void => {
    setMoved((previous) =>
      previous !== null && previous.index === index && previous.onPage === currentIndex
        ? previous
        : { index, onPage: currentIndex },
    );
  };

  return (index) => ({
    ref: refFor(index),
    tabIndex: index === stop ? 0 : -1,
    onFocus: () => {
      moveTo(index);
    },
    onKeyDown: (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      const target = rovingTarget(event.key, index, count);
      if (target === null) {
        return;
      }
      // Arrow keys and Home/End scroll the page by default; inside the list they move instead.
      event.preventDefault();
      moveTo(target);
      elements.get(target)?.focus();
    },
  });
}
